import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import MediaCarousel from "@/components/MediaCarousel";

/**
 * Iter54b + Iter54b-ext — HeroCarousel
 * ────────────────────────────────────────────────────────────────────────────
 * Thin wrapper around <MediaCarousel /> that supplies hero-specific slide
 * rendering. All carousel behaviour lives in the generic primitive so it can
 * be reused for Featured Plans, Testimonials, Blogs, Interactive Experiences
 * etc. without duplicating the auto-rotate / swipe / dot / arrow logic.
 *
 * Iter54b-ext additions handled here:
 *   • runtime scheduling — filter slides by publish_at/expires_at
 *   • media resolution — image / video_upload / video_youtube / video_vimeo
 *     (tour_360 / matterport / coohom / walkthrough / ai_video / external_embed
 *     are schema-reserved; fall back to a placeholder until UI ships)
 *   • CTA type resolver — convert (type, value) → href
 *   • analytics tracking — impression on slide activation + click on CTA
 */

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-4">
    <span className="hairline-accent w-10 shrink-0" />
    <span className="overline text-accent">{children}</span>
  </div>
);

// ── URL parsers for external videos ──────────────────────────────────────────
const YT_RE = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/;
const VIMEO_RE = /vimeo\.com\/(?:video\/)?([0-9]+)/;

const youtubeEmbed = (url) => {
  const m = String(url || "").match(YT_RE);
  if (!m) return null;
  const id = m[1];
  return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0&playsinline=1`;
};
const vimeoEmbed = (url) => {
  const m = String(url || "").match(VIMEO_RE);
  if (!m) return null;
  return `https://player.vimeo.com/video/${m[1]}?autoplay=1&muted=1&loop=1&background=1`;
};

// ── CTA resolver ─────────────────────────────────────────────────────────────
// Given a CTA type + value, return the destination `href`. For now most
// types map to a plain URL entered by admin; the enum lets us evolve the
// admin UI (e.g. plan picker) without a data migration.
const resolveCtaHref = (cta) => {
  if (!cta) return null;
  // Legacy single-field CTAs — still supported.
  if (typeof cta === "string") return cta;
  const value = (cta.href || cta.value || "").trim();
  if (!value) return null;
  switch (cta.type) {
    case "plan":       return value.startsWith("/") ? value : `/plans/${value}`;
    case "collection": return value.startsWith("/") ? value : `/plan-collections/${value}`;
    case "category":   return value.startsWith("/") ? value : `/plans?style=${encodeURIComponent(value)}`;
    case "blog":       return value.startsWith("/") ? value : `/blog/${value}`;
    case "walkthrough":
    case "tour_360":
    case "matterport":
    case "coohom":     return value; // absolute URL to external tour
    case "contact":    return value.startsWith("/") ? value : "/contact";
    case "external":   return value;
    case "internal":
    default:           return value; // treat as plain path/URL
  }
};

// ── Media resolver — image / upload video / youtube / vimeo / placeholder ────
const HeroMedia = ({ slide, isActive, isFirst, transitionClass }) => {
  const type = slide.media_type || "image";
  const primary = slide.media_url || "";
  const provider = slide.media_provider_url || "";
  const alt = slide.seo?.alt_text || slide.heading_line1 || "Homepage slide";
  const title = slide.seo?.image_title || undefined;

  // Extensible switch — schema-only types render a discreet placeholder so
  // saving them today doesn't blank the hero.
  if (type === "video_youtube" && youtubeEmbed(provider)) {
    return (
      <iframe
        src={youtubeEmbed(provider)}
        title={title || alt}
        className={`absolute inset-0 w-full h-full pointer-events-none ${transitionClass || ""}`}
        style={{ border: 0 }}
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        loading={isFirst ? "eager" : "lazy"}
      />
    );
  }
  if (type === "video_vimeo" && vimeoEmbed(provider)) {
    return (
      <iframe
        src={vimeoEmbed(provider)}
        title={title || alt}
        className={`absolute inset-0 w-full h-full pointer-events-none ${transitionClass || ""}`}
        style={{ border: 0 }}
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        loading={isFirst ? "eager" : "lazy"}
      />
    );
  }
  if (type === "video_upload" || type === "video") {
    return (
      <video
        src={primary}
        poster={slide.poster_url || undefined}
        autoPlay
        muted
        loop
        playsInline
        preload={isFirst ? "auto" : "none"}
        title={title}
        className={`absolute inset-0 w-full h-full object-cover ${transitionClass || ""}`}
      />
    );
  }
  // Default = image (also renders a poster for future tour_* types).
  const src = primary || slide.poster_url;
  if (!src) {
    return <div className={`absolute inset-0 w-full h-full bg-primary/80 ${transitionClass || ""}`} />;
  }
  return (
    <img
      src={src}
      alt={alt}
      title={title}
      loading={isFirst ? "eager" : "lazy"}
      className={`absolute inset-0 w-full h-full object-cover ${transitionClass || ""}`}
    />
  );
};

// ── Scheduling filter ────────────────────────────────────────────────────────
// A slide is live if enabled AND now ∈ [publish_at, expires_at].
const isSlideLive = (slide, now = new Date()) => {
  if (!slide) return false;
  if (slide.enabled === false) return false;
  const start = slide.publish_at ? new Date(slide.publish_at) : null;
  const end = slide.expires_at ? new Date(slide.expires_at) : null;
  if (start && !Number.isNaN(start.getTime()) && now < start) return false;
  if (end && !Number.isNaN(end.getTime()) && now > end) return false;
  return true;
};

// ── Analytics fire-and-forget ────────────────────────────────────────────────
import { trackSectionEvent } from "@/lib/mediaHelpers";
const trackHeroEvent = (slideId, event) => trackSectionEvent("hero", slideId, event);

// ── Public component ─────────────────────────────────────────────────────────
const HeroCarousel = ({ slides, carousel }) => {
  const enabled = useMemo(
    () => (slides || []).filter((s) => isSlideLive(s) && (s.media_url || s.media_provider_url || s.poster_url)),
    [slides]
  );
  const impressedRef = useRef(new Set());

  if (enabled.length === 0) return null;

  const transition = (carousel && carousel.transition) || "kenburns";

  const renderSlide = ({ slide, index, isActive, isFirst }) => {
    const overlayOpacity = typeof slide.overlay_opacity === "number" ? slide.overlay_opacity : 0.55;

    const layerClass = {
      fade: `absolute inset-0 transition-opacity duration-[900ms] ease-out ${isActive ? "opacity-100 z-10" : "opacity-0 z-0"}`,
      slide: `absolute inset-0 transition-transform duration-[900ms] ease-out ${isActive ? "translate-x-0 z-10" : "translate-x-full z-0"}`,
      zoom: `absolute inset-0 transition-all duration-[900ms] ease-out ${isActive ? "opacity-100 scale-100 z-10" : "opacity-0 scale-105 z-0"}`,
      kenburns: `absolute inset-0 transition-opacity duration-[900ms] ease-out ${isActive ? "opacity-100 z-10" : "opacity-0 z-0"}`,
    }[transition] || "absolute inset-0";

    const mediaAnimClass = transition === "kenburns" && isActive ? "anim-slow-zoom" : "";

    const primaryHref = resolveCtaHref({ type: slide.primary_cta_type || "internal", href: slide.primary_cta_href });
    const secondaryHref = resolveCtaHref({ type: slide.secondary_cta_type || "internal", href: slide.secondary_cta_href });

    const onCtaClick = () => trackHeroEvent(slide.id, "cta_click");

    return (
      <div className={layerClass} aria-hidden={!isActive} data-testid={`hero-slide-${index}`}>
        <HeroMedia slide={slide} isActive={isActive} isFirst={isFirst} transitionClass={mediaAnimClass} />

        {/* Cinematic gradient */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,${overlayOpacity}) 45%, rgba(0,0,0,0.82) 100%)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent pointer-events-none hidden md:block" />

        {/* Copy — bottom-left */}
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-12 pb-16 lg:pb-24">
            <div className="max-w-3xl text-primary-foreground">
              {slide.eyebrow && (
                <div className="anim-fade-up anim-delay-1" data-testid={`hero-slide-eyebrow-${index}`}>
                  <Eyebrow>{slide.eyebrow}</Eyebrow>
                </div>
              )}
              {(slide.heading_line1 || slide.heading_italic || slide.heading_line3) && (
                <h1 className="font-display font-light leading-[0.92] tracking-tighter mt-7 text-5xl sm:text-6xl lg:text-7xl anim-fade-up anim-delay-2">
                  {slide.heading_line1}
                  {(slide.heading_italic || slide.heading_line3) && <br />}
                  {slide.heading_italic && <span className="italic text-accent">{slide.heading_italic}</span>}
                  {slide.heading_italic && slide.heading_line3 && " "}
                  {slide.heading_line3}
                </h1>
              )}
              {slide.description && (
                <p className="mt-7 text-base sm:text-lg lg:text-xl text-primary-foreground/85 max-w-xl leading-relaxed anim-fade-up anim-delay-3">
                  {slide.description}
                </p>
              )}
              {(slide.primary_cta_label || slide.secondary_cta_label) && (
                <div className="mt-10 flex flex-wrap gap-4 anim-fade-up anim-delay-4">
                  {slide.primary_cta_label && primaryHref && (
                    <Link to={primaryHref} onClick={onCtaClick}>
                      <Button
                        data-testid={`hero-slide-primary-${index}`}
                        size="lg"
                        className="lux-btn rounded-none bg-accent hover:bg-accent/95 text-accent-foreground h-14 px-9 text-base tracking-wide"
                      >
                        {slide.primary_cta_label} <ArrowRight size={18} className="ml-2" />
                      </Button>
                    </Link>
                  )}
                  {slide.secondary_cta_label && secondaryHref && (
                    <Link to={secondaryHref} onClick={onCtaClick}>
                      <Button
                        data-testid={`hero-slide-secondary-${index}`}
                        size="lg"
                        variant="outline"
                        className="lux-btn rounded-none h-14 px-9 border-primary-foreground/50 text-primary-foreground bg-transparent hover:bg-primary-foreground hover:text-primary text-base tracking-wide"
                      >
                        {slide.secondary_cta_label}
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {(slide.featured_chip_title || slide.featured_chip_subtitle) && (
          <div
            className="absolute top-8 right-8 lg:top-12 lg:right-12 hidden md:block anim-fade-up anim-delay-3 z-20"
            data-testid={`hero-slide-chip-${index}`}
          >
            <div className="bg-background/95 backdrop-blur border border-border p-5 max-w-[260px] shadow-2xl">
              {slide.featured_chip_title && <Eyebrow>{slide.featured_chip_title}</Eyebrow>}
              {slide.featured_chip_subtitle && (
                <div className="font-display text-xl mt-3 leading-snug text-foreground">{slide.featured_chip_subtitle}</div>
              )}
              {slide.featured_plan_id && (
                <Link
                  to={`/plans/${slide.featured_plan_id}`}
                  onClick={onCtaClick}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-accent hover:text-accent/80 transition-fast"
                >
                  View plan <ArrowUpRight size={12} />
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Optional SEO description — visually hidden but readable by crawlers. */}
        {slide.seo?.seo_description && (
          <span className="sr-only" data-testid={`hero-slide-seo-${index}`}>{slide.seo.seo_description}</span>
        )}
      </div>
    );
  };

  const onActiveChange = (i, s) => {
    if (!s || !s.id) return;
    if (impressedRef.current.has(s.id)) return;
    impressedRef.current.add(s.id);
    trackHeroEvent(s.id, "impression");
  };

  return (
    <MediaCarousel
      slides={enabled}
      renderSlide={renderSlide}
      config={carousel || {}}
      ariaLabel="Homepage hero"
      testidPrefix="hero"
      onActiveChange={onActiveChange}
    />
  );
};

export default HeroCarousel;
